/**
 * Structured Data (Schema.org JSON-LD)
 * 
 * Provides rich snippets for Google Search results.
 * Helps search engines understand your business better.
 * 
 * Types used:
 * - LocalBusiness: Company information
 * - Organization: Brand identity
 * - Service: Each service offered
 */

export const getOrganizationSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "DSI Durango",
  "alternateName": "Durango Systems & Integration",
  "url": "https://dsidurango.com",
  "logo": "https://dsidurango.com/images/logo/dsi-logo.png",
  "description": language === 'es'
    ? "Soluciones industriales en maquinado CNC, fixtures para arneses y automatización. Especialistas en G10, FR4 y materiales técnicos."
    : "Industrial solutions in CNC machining, harness fixtures, and automation. Specialists in G10, FR4, and technical materials.",
  "sameAs": [
    "https://www.linkedin.com/company/dsi-durango",
    // Add more social profiles
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+52-618-171-9959",
    "contactType": "sales",
    "areaServed": ["MX", "US"],
    "availableLanguage": ["English", "Spanish"]
  },
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Durango",
    "addressRegion": "Durango",
    "addressCountry": "MX"
  }
});

export const getLocalBusinessSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "DSI Durango",
  "image": "https://dsidurango.com/images/logo/dsi-logo.png",
  "description": language === 'es'
    ? "Empresa especializada en maquinado CNC de precisión, fabricación de fixtures para arneses y soluciones de automatización industrial."
    : "Company specialized in precision CNC machining, harness fixture manufacturing, and industrial automation solutions.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Durango",
    "addressLocality": "Durango",
    "addressRegion": "Durango",
    "postalCode": "34000",
    "addressCountry": "MX"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 24.0277,
    "longitude": -104.6532
  },
  "url": "https://dsidurango.com",
  "telephone": "+52-618-171-9959",
  "priceRange": "$$",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday"
    ],
    "opens": "08:00",
    "closes": "17:00"
  },
  "areaServed": [
    {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 24.0277,
        "longitude": -104.6532
      },
      "geoRadius": "2000000"
    }
  ]
});

export const getCNCServiceSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "CNC Machining",
  "provider": {
    "@type": "Organization",
    "name": "DSI Durango"
  },
  "areaServed": {
    "@type": "Country",
    "name": ["Mexico", "United States"]
  },
  "description": language === 'es'
    ? "Maquinado CNC de precisión en G10, FR4, fenólicos, aluminio y plásticos de ingeniería. Tolerancias de ±0.05mm."
    : "Precision CNC machining in G10, FR4, phenolics, aluminum, and engineering plastics. Tolerances of ±0.05mm.",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock",
    "priceSpecification": {
      "@type": "PriceSpecification",
      "priceCurrency": "USD"
    }
  }
});

export const getHarnessServiceSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Harness Fixture Manufacturing",
  "provider": {
    "@type": "Organization",
    "name": "DSI Durango"
  },
  "areaServed": {
    "@type": "Country",
    "name": ["Mexico", "United States"]
  },
  "description": language === 'es'
    ? "Diseño y fabricación de fixtures personalizados para ensamble de arneses. Integrador autorizado Dynalab."
    : "Custom fixture design and manufacturing for harness assembly. Authorized Dynalab integrator.",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock"
  }
});

export const getAutomationServiceSchema = (language = 'en') => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Industrial Automation",
  "provider": {
    "@type": "Organization",
    "name": "DSI Durango"
  },
  "areaServed": {
    "@type": "Country",
    "name": ["Mexico", "United States"]
  },
  "description": language === 'es'
    ? "Soluciones de automatización industrial con control PLC, sistemas de visión y estaciones de prueba."
    : "Industrial automation solutions with PLC control, vision systems, and testing stations.",
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock"
  }
});

export const getBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});
